/**
 * Ledger VAT endpoints (bos-api).
 *
 * Paths are relative to the axios client's base, which already ends in `/api/v1`
 * — so `/ledger/vat/report` reaches `…/api/v1/ledger/vat/report`. This mirrors
 * `AccountsEndpoints`, `JournalEndpoints` and `StatementsEndpoints`, the other
 * live ledger resources.
 *
 * What the client originally assumed and got wrong, all measured:
 *
 * - **The paths are `/ledger/vat/report` and `/ledger/vat/settlement`**, not
 *   `/vat/report` and `/vat/settlement/prepare`.
 * - **The range is `dateFrom`/`dateTo`**, not `from`/`to`. Unlike the journal's
 *   `DateFrom`/`DateTo`, these bind case-insensitively — but there is no reason
 *   to rely on that, so the spec's spelling is what goes on the wire.
 * - **Settlement is a POST with a JSON body**, not query parameters.
 *
 * Two defaults worth stating, because both fail silently:
 *
 * - **Omitting the range is not "all time".** The server binds the missing dates
 *   to `0001-01-01` and returns an all-zero report that is indistinguishable
 *   from a period with no VAT activity. Every read here sends both.
 * - **The default locale is `ka`**, whatever the app's language. Account names
 *   are resolved server-side, so the locale is part of the request and switching
 *   language refetches rather than re-resolving a map the client holds.
 */

const search = (entries: Record<string, string>): string => {
    const query = new URLSearchParams(entries).toString();
    return query ? `?${query}` : '';
};

export type VatReportQuery = {
    /** ISO `YYYY-MM-DD`, inclusive. */
    dateFrom: string;
    /** ISO `YYYY-MM-DD`, inclusive. */
    dateTo: string;
    /** Which locale the server should resolve every account `name` into. */
    locale: string;
};

export const VatEndpoints = {
    /** GET — VAT movement over a period, by account, with its composition. */
    report: ({ dateFrom, dateTo, locale }: VatReportQuery) =>
        `/ledger/vat/report${search({ dateFrom, dateTo, locale })}`,

    /**
     * POST — build the settlement entry for a period.
     *
     * A **builder, not a write**: the entry it returns is not persisted (a
     * `GET /ledger/journal-entries/{id}` on the returned id is a 404, and the
     * ledger's entry count does not move). Nothing reaches the ledger until the
     * entry form saves and posts it through the normal validated path.
     */
    settlement: () => '/ledger/vat/settlement',
};
